import { ActionTree } from 'vuex';
import { UpsellStatusEnum } from '@modules/husleie-framework/src/types/Upsell.entity';
import UpsellTenancySubscriptionState from '@modules/store/upsell/types/UpsellTenancySubscriptionState.entity';
import type CommonState from '@modules/store/common/types/CommonState.entity';
import api from '@modules/store/upsell/api/index.api';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import {
  TenancySubscriptionGetDetails,
  TenancySubscriptionGet,
  TenancySubscriptionCreate,
} from '@modules/store/upsell/types/UpsellAPI.interface';
import { UserRoleEnum } from '@modules/husleie-framework/src/types/User.entity';

export const actions: ActionTree<UpsellTenancySubscriptionState, CommonState> = {
  fetchTenancySubscription: ({ commit }, get: TenancySubscriptionGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setUpsellTenancySubscriptionLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setUpsellTenancySubscriptionApiError', false);

    return new api()
      .getTenancySubscription(get)
      .then((data: any) => {
        commit('setUpsellTenancySubscription', data.upsellTenancySubscription);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setUpsellTenancySubscriptionApiError', true);
        LogOperations.error('Upsell|TenancySubscription|Actions|fetchTenancySubscription', error.message);
        throw new Error('fetchTenancySubscription');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setUpsellTenancySubscriptionLoading', false);
      });
  },

  createTenancySubscription: ({ commit }, create: TenancySubscriptionCreate) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setUpsellTenancySubscriptionLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setUpsellTenancySubscriptionApiError', false);
    return new api()
      .createTenancySubscription(create)
      .then((data: any) => {
        commit('setUpsellTenancySubscriptionUuid', data.upsellId);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setUpsellTenancySubscriptionApiError', true);
        LogOperations.error('Upsell|TenancySubscription|Actions|createDepositSubscription', error.message);
        throw new Error('createTenancySubscription');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setUpsellTenancySubscriptionLoading', false);
      });
  },

  fetchTenancySubscriptionDetails: ({ commit }, get: TenancySubscriptionGetDetails) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('common/staticData/setApiError', { error: false }, { root: true });
    return new api()
      .getTenancySubscriptionDetails(get)
      .then((data: any) => {
        commit('setTenancySubscriptionDetails', data.upsell);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        LogOperations.error('Upsell|TenancySubscription|Actions|fetchTenancySubscriptionDetails', error.message);
        throw new Error('fetchTenancySubscriptionDetails');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
      });
  },

  assignTenancySubscriptionOpportunity: async (
    { commit, dispatch, getters },
    { tenancySubscriptionOpportunity, contractUuid, sessionType }
  ) => {
    commit('setUpsellTenancySubscriptionLoaded', false);
    commit('setUpsellTenancySubscriptionUuid', contractUuid);
    commit('setUpsellTenancySubscriptionStatus', tenancySubscriptionOpportunity.status);

    if (
      tenancySubscriptionOpportunity.status !== UpsellStatusEnum.COMPLETE &&
      tenancySubscriptionOpportunity.status !== UpsellStatusEnum.DECLINED
    ) {
      await dispatch('fetchTenancySubscriptionDetails', {
        uuid: contractUuid,
        userType: UserRoleEnum.LANDLORD,
        sessionType: sessionType,
      });
      let uuid = tenancySubscriptionOpportunity.uuid;
      if (!uuid) {
        await dispatch('createTenancySubscription', {
          token: getters.getUpsellTenancySubscriptionDetails.token,
        });
        uuid = getters.getUpsellTenancySubscriptionUuid;
      }
      await dispatch('fetchTenancySubscription', { uuid });

      commit('setUpsellTenancySubscriptionLoaded', true);
    }
  },
};
